import React from "react";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

interface DataPoint {
  name: string;
  value: number;
  description: string;
  icon: string;
}

interface SlideContent {
  title?: string;
  subtitle?: string;
  data?: string | DataPoint[];
  teacherNote?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const defaultData: DataPoint[] = [
  {
    name: "Skill A",
    value: 85,
    description: "Core competency level",
    icon: "⭐",
  },
  {
    name: "Skill B",
    value: 70,
    description: "Technical proficiency",
    icon: "💫",
  },
  {
    name: "Skill C",
    value: 90,
    description: "Overall performance",
    icon: "🌟",
  },
];

const EditableDiv: React.FC<{
  text: string;
  onEdit: (value: string) => void;
  className?: string;
}> = ({ text, onEdit, className = "" }) => {
  return (
    <div
      contentEditable
      suppressContentEditableWarning
      onBlur={(e) => onEdit(e.currentTarget.textContent || "")}
      className={`outline-none ${className}`}
    >
      {text}
    </div>
  );
};

const DataSlide4 = {
  id: "data-slide4",
  name: "Data Visualization (Radar Chart)",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
      defaultValue: "Skill Assessment",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
      defaultValue: "Competency Overview",
    },
    {
      id: "data",
      label: "Data",
      type: "text",
      defaultValue: JSON.stringify(defaultData),
    },
    {
      id: "teacherNote",
      label: "Teacher Note",
      type: "text",
      defaultValue: "Evaluate strengths and areas for improvement.",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
      defaultValue: "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)",
    },
  ],
  render: (content: SlideContent = {}) => {
    const {
      title = "Skill Assessment",
      subtitle = "Competency Overview",
      teacherNote = "Evaluate strengths and areas for improvement.",
      bgColor = "linear-gradient(135deg, #0f766e 0%, #0d9488 100%)",
      data = JSON.stringify(defaultData),
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);
    };

    let parsedData = defaultData;
    try {
      if (typeof data === "string") {
        parsedData = JSON.parse(data);
      }
    } catch (error) {
      console.warn("Error parsing data:", error);
    }

    const CustomTooltip: React.FC<{
      active?: boolean;
      payload?: Array<any>;
    }> = ({ active, payload }) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
          <div className="bg-white/90 p-3 rounded-lg shadow-lg">
            <div className="flex items-center gap-2">
              <span className="text-xl">{data.icon}</span>
              <span className="font-semibold">{data.name}</span>
            </div>
            <p className="text-sm mt-1">{data.description}</p>
            <div className="font-bold mt-1">{data.value}%</div>
          </div>
        );
      }
      return null;
    };

    const handleDataEdit = (index: number, field: string, value: string) => {
      let parsedData = defaultData;
      try {
        if (typeof data === "string") {
          parsedData = JSON.parse(data);
        }
      } catch (error) {
        console.warn("Error parsing data:", error);
      }

      const updatedData = [...parsedData];
      updatedData[index] = {
        ...updatedData[index],
        [field]: value,
      };

      handleEdit("data")(JSON.stringify(updatedData));
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "linear-gradient(45deg, rgba(255, 255, 255, 0.1) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0.1) 75%, transparent 75%, transparent)",
              backgroundSize: "100px 100px",
            }}
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col h-full p-6">
          {/* Header */}
          <div className="text-center mb-4">
            <EditableDiv
              text={title}
              onEdit={handleEdit("title")}
              className="text-3xl font-bold mb-1 bg-clip-text text-transparent bg-gradient-to-r from-teal-300 to-emerald-300"
            />
            <EditableDiv
              text={subtitle}
              onEdit={handleEdit("subtitle")}
              className="text-xl text-white/80"
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 flex">
            {/* Chart */}
            <div className="w-2/3 h-full bg-white/5 rounded-xl p-4 backdrop-blur-sm">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart
                  cx="50%"
                  cy="50%"
                  outerRadius="80%"
                  data={parsedData}
                >
                  <PolarGrid stroke="rgba(255,255,255,0.2)" />
                  <PolarAngleAxis
                    dataKey="name"
                    tick={{ fill: "#fff", fontSize: 12 }}
                  />
                  <PolarRadiusAxis
                    angle={30}
                    domain={[0, 100]}
                    tick={{ fill: "#fff" }}
                  />
                  <Radar
                    name="Values"
                    dataKey="value"
                    stroke="#2dd4bf"
                    fill="#2dd4bf"
                    fillOpacity={0.5}
                  />
                  <Tooltip content={<CustomTooltip />} />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Description */}
            <div className="w-1/3 pl-4 flex flex-col justify-center">
              {parsedData.map((item, index) => (
                <div
                  key={index}
                  className="mb-4 bg-white/10 rounded-lg p-3 backdrop-blur-sm"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl">{item.icon}</span>
                    <EditableDiv
                      text={item.name}
                      onEdit={(value) => handleDataEdit(index, "name", value)}
                      className="font-semibold"
                    />
                    <EditableDiv
                      text={`${item.value}%`}
                      onEdit={(value) =>
                        handleDataEdit(index, "value", value.replace("%", ""))
                      }
                      className="ml-auto font-bold"
                    />
                  </div>
                  <EditableDiv
                    text={item.description}
                    onEdit={(value) =>
                      handleDataEdit(index, "description", value)
                    }
                    className="text-sm text-white/80"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Teacher Note */}
          <div className="mt-4 text-center">
            <EditableDiv
              text={teacherNote}
              onEdit={handleEdit("teacherNote")}
              className="text-sm text-white/70 italic"
            />
          </div>
        </div>
      </div>
    );
  },
};

export default DataSlide4;
